import React,{useCallback,useEffect,useState}from"react";
import{Box,Button,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,IconButton,TextField,Typography}from"@mui/material";
import AddRounded from"@mui/icons-material/AddRounded";
import CloseRounded from"@mui/icons-material/CloseRounded";
import EditOutlined from"@mui/icons-material/EditOutlined";
import DeleteOutlineOutlined from"@mui/icons-material/DeleteOutlineOutlined";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const initialEmailForm={email:"",active:true};

export default function AdminNotifications(){
  const{authFetch,user}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();

  const[settings,setSettings]=useState([]);
  const[emails,setEmails]=useState([]);
  const[loading,setLoading]=useState(true);
  const[actionId,setActionId]=useState(null);

  const[emailDialogOpen,setEmailDialogOpen]=useState(false);
  const[editingEmail,setEditingEmail]=useState(null);
  const[emailForm,setEmailForm]=useState(initialEmailForm);
  const[emailSaving,setEmailSaving]=useState(false);

  const[deleteItem,setDeleteItem]=useState(null);
  const[deleting,setDeleting]=useState(false);

  const isAdmin=user?.role==="ADMIN";

  const loadData=useCallback(async()=>{
    if(!isAdmin)return;

    setLoading(true);

    try{
      const[settingsResponse,emailsResponse]=await Promise.all([
        authFetch("/api/login/configuration/notification-settings/"),
        authFetch("/api/login/configuration/notification-emails/"),
      ]);

      const settingsData=await settingsResponse.json();
      const emailsData=await emailsResponse.json();

      if(!settingsResponse.ok){
        throw new Error(settingsData.detail||"No se pudo cargar la configuración de notificaciones.");
      }

      if(!emailsResponse.ok){
        throw new Error(emailsData.detail||"No se pudieron cargar los correos de notificación.");
      }

      setSettings(Array.isArray(settingsData)?settingsData:[]);
      setEmails(Array.isArray(emailsData)?emailsData:[]);
    }catch(error){
      showError(error.message||"No se pudo cargar la configuración de notificaciones.");
    }finally{
      setLoading(false);
    }
  },[authFetch,isAdmin,showError]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const updateSetting=async(item,field)=>{
    const action=`${item.event}-${field}`;
    const newValue=!item[field];

    setActionId(action);

    try{
      const response=await authFetch(`/api/login/configuration/notification-settings/${item.event}/`,{
        method:"PATCH",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          [field]:newValue,
        }),
      });

      const data=await response.json();

      if(!response.ok){
        throw new Error(data.detail||"No se pudo actualizar la configuración.");
      }

      setSettings(prev=>prev.map(current=>current.event===data.event?data:current));
    }catch(error){
      showError(error.message||"No se pudo actualizar la configuración.");
    }finally{
      setActionId(null);
    }
  };

  const openCreateEmail=()=>{
    setEditingEmail(null);
    setEmailForm(initialEmailForm);
    setEmailDialogOpen(true);
  };

  const openEditEmail=(item)=>{
    setEditingEmail(item);
    setEmailForm({
      email:item.email||"",
      active:Boolean(item.active),
    });
    setEmailDialogOpen(true);
  };

  const closeEmailDialog=()=>{
    if(emailSaving)return;
    setEmailDialogOpen(false);
    setEditingEmail(null);
    setEmailForm(initialEmailForm);
  };

  const handleEmailChange=(e)=>{
    const{name,value,checked,type}=e.target;
    setEmailForm(prev=>({...prev,[name]:type==="checkbox"?checked:value}));
  };

  const saveEmail=async()=>{
    const email=emailForm.email.trim().toLowerCase();

    if(!email){
      showWarning("Ingrese un correo electrónico.");
      return;
    }

    setEmailSaving(true);

    try{
      const response=await authFetch(
        editingEmail
          ?`/api/login/configuration/notification-emails/${editingEmail.public_id}/`
          :"/api/login/configuration/notification-emails/",
        {
          method:editingEmail?"PATCH":"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            email,
            active:emailForm.active,
          }),
        }
      );

      const data=await response.json();

      if(!response.ok){
        throw new Error(data.detail||"No se pudo guardar el correo.");
      }

      showSuccess(editingEmail?"Correo actualizado correctamente.":"Correo agregado correctamente.");
      setEmailDialogOpen(false);
      setEditingEmail(null);
      setEmailForm(initialEmailForm);
      await loadData();
    }catch(error){
      showError(error.message||"No se pudo guardar el correo.");
    }finally{
      setEmailSaving(false);
    }
  };

  const toggleEmailStatus=async(item)=>{
    setActionId(`email-status-${item.public_id}`);

    try{
      const response=await authFetch(`/api/login/configuration/notification-emails/${item.public_id}/`,{
        method:"PATCH",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          active:!item.active,
        }),
      });

      const data=await response.json();

      if(!response.ok){
        throw new Error(data.detail||"No se pudo cambiar el estado del correo.");
      }

      setEmails(prev=>prev.map(current=>current.public_id===data.public_id?data:current));
      showSuccess(item.active?"Correo inactivado correctamente.":"Correo activado correctamente.");
    }catch(error){
      showError(error.message||"No se pudo cambiar el estado del correo.");
    }finally{
      setActionId(null);
    }
  };

  const deleteEmail=async()=>{
    if(!deleteItem)return;

    setDeleting(true);

    try{
      const response=await authFetch(`/api/login/configuration/notification-emails/${deleteItem.public_id}/`,{
        method:"DELETE",
      });

      const data=await response.json();

      if(!response.ok){
        throw new Error(data.detail||"No se pudo eliminar el correo.");
      }

      setEmails(prev=>prev.filter(item=>item.public_id!==deleteItem.public_id));
      setDeleteItem(null);
      showSuccess("Correo eliminado correctamente.");
    }catch(error){
      showError(error.message||"No se pudo eliminar el correo.");
    }finally{
      setDeleting(false);
    }
  };

  const fieldSx={
    "& .MuiOutlinedInput-root":{
      borderRadius:2,
    },
  };

  if(!isAdmin){
    return(
      <Typography sx={{fontSize:14,color:"#667085"}}>
        No tiene acceso a esta configuración.
      </Typography>
    );
  }

  return(
    <Box>
      <Typography sx={{fontSize:24,fontWeight:700,color:"#182230",mb:2.5}}>
        Notificaciones
      </Typography>

      <Typography sx={{fontSize:16,fontWeight:700,color:"#182230",mb:1.5}}>
        Configuración por evento
      </Typography>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,overflowX:"auto",mb:3.5}}>
        <Box sx={{minWidth:900}}>
          <Box sx={{display:"grid",gridTemplateColumns:"1.7fr 1fr 1fr 1fr 1.4fr",gap:2,px:2.5,py:1.5,bgcolor:"#123f6d",color:"#fff"}}>
            {["Evento","Administrador","Técnico","Contactos","Correos adicionales"].map(label=>(
              <Typography key={label} sx={{fontSize:13,fontWeight:700}}>
                {label}
              </Typography>
            ))}
          </Box>

          {loading&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>
                Cargando configuración...
              </Typography>
            </Box>
          )}

          {!loading&&settings.map(item=>(
            <Box
              key={item.event}
              sx={{
                display:"grid",
                gridTemplateColumns:"1.7fr 1fr 1fr 1fr 1.4fr",
                gap:2,
                px:2.5,
                py:1.2,
                alignItems:"center",
                borderBottom:"1px solid #edf1f5",
                "&:last-child":{borderBottom:"none"},
              }}
            >
              <Typography sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>
                {item.event_display}
              </Typography>

              <Checkbox
                size="small"
                checked={Boolean(item.notify_administrator)}
                disabled={actionId!==null}
                onChange={()=>updateSetting(item,"notify_administrator")}
                sx={{width:"fit-content"}}
              />

              <Checkbox
                size="small"
                checked={Boolean(item.notify_technician)}
                disabled={actionId!==null}
                onChange={()=>updateSetting(item,"notify_technician")}
                sx={{width:"fit-content"}}
              />

              <Checkbox
                size="small"
                checked={Boolean(item.notify_contacts)}
                disabled={actionId!==null}
                onChange={()=>updateSetting(item,"notify_contacts")}
                sx={{width:"fit-content"}}
              />

              <Checkbox
                size="small"
                checked={Boolean(item.notify_additional_emails)}
                disabled={actionId!==null}
                onChange={()=>updateSetting(item,"notify_additional_emails")}
                sx={{width:"fit-content"}}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:2,mb:1.5}}>
        <Typography sx={{fontSize:16,fontWeight:700,color:"#182230"}}>
          Correos adicionales
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddRounded/>}
          onClick={openCreateEmail}
          size="small"
          sx={{
            bgcolor:"#123f6d",
            borderRadius:2,
            textTransform:"none",
            fontWeight:700,
            boxShadow:"none",
            "&:hover":{
              bgcolor:"#0e345b",
              boxShadow:"none",
            },
          }}
        >
          Agregar correo
        </Button>
      </Box>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,overflowX:"auto"}}>
        <Box sx={{minWidth:720}}>
          <Box sx={{display:"grid",gridTemplateColumns:"2.2fr .8fr 1.2fr",gap:2,px:2.5,py:1.5,bgcolor:"#123f6d",color:"#fff"}}>
            {["Correo","Estado","Acciones"].map(label=>(
              <Typography key={label} sx={{fontSize:13,fontWeight:700}}>
                {label}
              </Typography>
            ))}
          </Box>

          {loading&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>
                Cargando correos...
              </Typography>
            </Box>
          )}

          {!loading&&emails.length===0&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>
                No hay correos adicionales registrados.
              </Typography>
            </Box>
          )}

          {!loading&&emails.map(item=>(
            <Box
              key={item.public_id}
              sx={{
                display:"grid",
                gridTemplateColumns:"2.2fr .8fr 1.2fr",
                gap:2,
                px:2.5,
                py:1.5,
                alignItems:"center",
                borderBottom:"1px solid #edf1f5",
                "&:last-child":{borderBottom:"none"},
              }}
            >
              <Typography sx={{fontSize:13.5,color:"#344054",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {item.email}
              </Typography>

              <Box sx={{width:"fit-content",px:1.1,py:.4,borderRadius:5,bgcolor:item.active?"#e9f7ef":"#fdecec",color:item.active?"#207a45":"#b42318",fontSize:12,fontWeight:700}}>
                {item.active?"Activo":"Inactivo"}
              </Box>

              <Box sx={{display:"flex",gap:.5,alignItems:"center"}}>
                <IconButton
                  size="small"
                  onClick={()=>openEditEmail(item)}
                  disabled={actionId!==null}
                  title="Editar"
                >
                  <EditOutlined sx={{fontSize:18}}/>
                </IconButton>

                <IconButton
                  size="small"
                  onClick={()=>setDeleteItem(item)}
                  disabled={actionId!==null}
                  title="Eliminar"
                  sx={{color:"#b42318"}}
                >
                  <DeleteOutlineOutlined sx={{fontSize:18}}/>
                </IconButton>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionId!==null}
                  onClick={()=>toggleEmailStatus(item)}
                  sx={{
                    borderRadius:2,
                    textTransform:"none",
                    fontWeight:700,
                    fontSize:11.5,
                    color:item.active?"#b42318":"#207a45",
                    borderColor:item.active?"#f0b7b3":"#a9d8bc",
                    "&:hover":{
                      borderColor:item.active?"#b42318":"#207a45",
                      bgcolor:item.active?"#fff6f5":"#f0faf4",
                    },
                  }}
                >
                  {actionId===`email-status-${item.public_id}`?"Procesando...":item.active?"Inactivar":"Activar"}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog
        open={emailDialogOpen}
        onClose={closeEmailDialog}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:3,
            boxShadow:"none",
            border:"1px solid #dce3ea",
          },
        }}
      >
        <DialogTitle sx={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:20,fontWeight:700,color:"#182230"}}>
          {editingEmail?"Editar correo":"Agregar correo"}

          <IconButton onClick={closeEmailDialog} disabled={emailSaving}>
            <CloseRounded/>
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{pt:1}}>
            <TextField
              label="Correo electrónico"
              name="email"
              type="email"
              value={emailForm.email}
              onChange={handleEmailChange}
              size="small"
              fullWidth
              sx={fieldSx}
            />

            <FormControlLabel
              sx={{mt:1.5}}
              control={
                <Checkbox
                  name="active"
                  checked={emailForm.active}
                  onChange={handleEmailChange}
                  size="small"
                />
              }
              label={
                <Typography sx={{fontSize:13.5}}>
                  Correo activo
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5}}>
          <Button
            onClick={closeEmailDialog}
            disabled={emailSaving}
            sx={{
              textTransform:"none",
              color:"#667085",
              fontWeight:700,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveEmail}
            disabled={emailSaving}
            sx={{
              bgcolor:"#123f6d",
              borderRadius:2,
              textTransform:"none",
              fontWeight:700,
              boxShadow:"none",
              "&:hover":{
                bgcolor:"#0e345b",
                boxShadow:"none",
              },
            }}
          >
            {emailSaving?"Guardando...":editingEmail?"Guardar cambios":"Agregar correo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteItem)}
        onClose={()=>!deleting&&setDeleteItem(null)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:3,
            boxShadow:"none",
            border:"1px solid #dce3ea",
          },
        }}
      >
        <DialogTitle sx={{fontSize:20,fontWeight:700,color:"#182230"}}>
          Eliminar correo
        </DialogTitle>

        <DialogContent>
          <Typography sx={{fontSize:13.5,color:"#475467"}}>
            ¿Desea eliminar el correo {deleteItem?.email}?
          </Typography>
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5}}>
          <Button
            onClick={()=>setDeleteItem(null)}
            disabled={deleting}
            sx={{
              textTransform:"none",
              color:"#667085",
              fontWeight:700,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={deleteEmail}
            disabled={deleting}
            sx={{
              bgcolor:"#b42318",
              borderRadius:2,
              textTransform:"none",
              fontWeight:700,
              boxShadow:"none",
              "&:hover":{
                bgcolor:"#912018",
                boxShadow:"none",
              },
            }}
          >
            {deleting?"Eliminando...":"Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}